import { Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { ENVIRONMENT } from 'src/app/providers/environment';
import { renderMarkdown } from 'src/app/shared/markdown';

/**
 * Help-article markdown, rendered the way merchants will read it. The admin editor previews with it
 * and the support chat (GOO-457) shows agent replies with it, so both look the same.
 */
@Component({
  selector: 'app-markdown',
  standalone: true,
  template: `<div class="app-markdown" [innerHTML]="html()" data-cy="markdown"></div>`,
  // The HTML comes from `innerHTML`, which emulated encapsulation doesn't reach.
  encapsulation: ViewEncapsulation.None,
  styles: `
    .app-markdown {
      line-height: 1.6;
      overflow-wrap: anywhere;

      > :first-child {
        margin-top: 0;
      }

      h1,
      h2,
      h3,
      h4 {
        font-weight: 600;
        line-height: 1.3;
        margin: 1.25em 0 0.5em;
      }

      h1 {
        font-size: 1.5rem;
      }

      h2 {
        font-size: 1.25rem;
      }

      h3 {
        font-size: 1.125rem;
      }

      p,
      ul,
      ol,
      pre,
      blockquote,
      table {
        margin: 0 0 0.75em;
      }

      ul {
        list-style: disc;
        padding-left: 1.5em;
      }

      ol {
        list-style: decimal;
        padding-left: 1.5em;
      }

      a {
        color: #2563eb;
        text-decoration: underline;
      }

      code {
        background: #f3f4f6;
        border-radius: 4px;
        font-size: 0.875em;
        padding: 0.1em 0.3em;
      }

      pre {
        background: #f3f4f6;
        border-radius: 6px;
        overflow-x: auto;
        padding: 0.75em;

        code {
          background: none;
          padding: 0;
        }
      }

      blockquote {
        border-left: 3px solid #d1d5db;
        color: #4b5563;
        padding-left: 0.75em;
      }

      img {
        border-radius: 6px;
        height: auto;
        max-width: 100%;
      }

      table {
        border-collapse: collapse;

        th,
        td {
          border: 1px solid #e5e7eb;
          padding: 0.25em 0.5em;
          text-align: left;
        }
      }

      hr {
        border-top: 1px solid #e5e7eb;
        margin: 1em 0;
      }
    }
  `,
})
export class MarkdownComponent {
  private readonly env = inject(ENVIRONMENT);

  readonly markdown = input<string | null | undefined>('');

  readonly html = computed(() => renderMarkdown(this.markdown(), this.env.frontDomain));
}
